import { ENTER } from '@angular/cdk/keycodes';
import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { LinkType, LinkUtils, Type, TypeButtons } from '@app-model/businessCard';
import { Client } from '@app-model/client';
import { LinkContents } from '@app-model/businessCard';
import { ProductService } from '@app-services/product.service';
import { PHONE_COUNTRY_OPTIONS, PhoneNumberUtils } from '@app-utils/phone-number';

@Component({
	selector: 'app-drag-drop-links',
	templateUrl: './drag-drop-links.component.html',
	styleUrls: ['./drag-drop-links.component.scss']
})
export class DragDropLinksComponent implements OnInit {
	allButtons: TypeButtons[] = [];
	readonly phoneCountryOptions = PHONE_COUNTRY_OPTIONS;

	@Input() initialFormValue: Client;
	@Input() typeForm: Type;
	@Output() updateFormArray: EventEmitter<void> = new EventEmitter<void>();

	@Output() inputChange: EventEmitter<{ button: any; value: string }> = new EventEmitter<{
		button: any;
		value: string;
	}>();
	@Output() blurEvent: EventEmitter<void> = new EventEmitter<void>();
	@Output() updateLinksOrderTwo: EventEmitter<LinkContents[]> = new EventEmitter<LinkContents[]>();

	constructor(private productService: ProductService) {}

	public ngOnInit() {
		this.productService.getProductInfo().subscribe(productInfo => {
			this.allButtons = LinkUtils.buildLinksArray(productInfo);
			if (
				this.initialFormValue &&
				this.initialFormValue.cardholderInfo &&
				this.initialFormValue.cardholderInfo.links &&
				this.typeForm === Type.INDIVIDUAL
			) {
				const linksArrayControl = this.initialFormValue.cardholderInfo.links;

				this.allButtons = this._organizeAndFillTempUrl(this.allButtons, linksArrayControl);
			}

			if (this.initialFormValue && this.initialFormValue.footerLinks && this.typeForm === Type.ENTERPRISE) {
				const linksArrayControl = this.initialFormValue.footerLinks;

				this.allButtons = this._organizeAndFillTempUrl(this.allButtons, linksArrayControl);
			}
		});
	}

	public noReturnPredicate(): boolean {
		return false;
	}

	public drop(event: CdkDragDrop<any[]>): void {
		if (event.previousContainer === event.container) {
			moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
		} else {
			transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
		}

		this.updateFormArray.emit();
		this.saveLinksOrder();
	}

	private _organizeAndFillTempUrl(allButtons: TypeButtons[], allbuttonsLinks: LinkContents[]): TypeButtons[] {
		let updatedButtons: TypeButtons[] = allButtons.map((button, index) => ({
			...button,
			tempUrl: '',
			phoneCountryCode: '',
			selectedButton: index + 1
		}));

		if (allbuttonsLinks && allbuttonsLinks.length) {
			updatedButtons = this._mapLinksToButtons(updatedButtons, allbuttonsLinks);
		} else {
			return updatedButtons;
		}

		return updatedButtons;
	}

	private _mapLinksToButtons(allButtons: TypeButtons[], allbuttons2: LinkContents[]): TypeButtons[] {
		const normalizedLinks = LinkUtils.normalizeLinkContents(allbuttons2);
		const updatedButtons: TypeButtons[] = [];

		normalizedLinks.forEach(button2 => {
			const foundButton = allButtons.find(button => LinkUtils.isSameLinkType(button?.type, button2?.type));

			if (foundButton) {
				const parsedPhoneNumber = LinkUtils.isPhoneLinkType(button2?.type)
					? PhoneNumberUtils.parseStoredPhoneNumber(button2.url)
					: undefined;

				updatedButtons.push({
					...foundButton,
					tempUrl: parsedPhoneNumber ? parsedPhoneNumber.localNumber : button2.url,
					phoneCountryCode: parsedPhoneNumber?.countryCode || ''
				});
			}
		});

		allButtons.forEach(button => {
			if (!normalizedLinks.some(button2 => LinkUtils.isSameLinkType(button2?.type, button?.type))) {
				updatedButtons.push(button);
			}
		});

		return updatedButtons;
	}

	public saveLinksOrder(): void {
		const orderedLinks = this.allButtons
			.filter(button => button.tempUrl !== '')
			.map(button => {
				const normalizedType = LinkUtils.normalizeLinkType(button.type);
				const url = LinkUtils.isPhoneLinkType(normalizedType)
					? PhoneNumberUtils.buildStoredPhoneNumber(button.phoneCountryCode, button.tempUrl)
					: button.tempUrl;

				return { type: normalizedType, url };
			});

		this.updateLinksOrderTwo.emit(orderedLinks);
	}

	public onLinkInputChange(button: TypeButtons, value: string): void {
		button.tempUrl = value;
		this.saveLinksOrder();
	}

	public onPhoneCountryChange(button: TypeButtons, value: string): void {
		button.phoneCountryCode = value;
		this.saveLinksOrder();
	}

	public isPhoneButton(button: TypeButtons): boolean {
		return LinkUtils.isPhoneLinkType(button?.type);
	}

	public showPhoneCountryError(button: TypeButtons): boolean {
		return (
			this.isPhoneButton(button) &&
			!!button?.tempUrl?.trim() &&
			!PhoneNumberUtils.isInternationalNumber(button?.tempUrl) &&
			!button?.phoneCountryCode
		);
	}

	public showPhoneNumberError(button: TypeButtons): boolean {
		return this.isPhoneButton(button) && !!button?.phoneCountryCode && !button?.tempUrl?.trim();
	}
}
